import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';

/**
 * Open a policy URL (Terms of Use, Privacy Policy, etc.) in a new tab on
 * web or in an in-app browser on native.
 *
 * NOTE: on web, PolicyLink no longer routes through this — it renders a real
 * anchor (`href` on RN-web Text), because `window.open` is silently dropped by
 * the native WebViews (no WKWebView UI delegate). This helper now effectively
 * serves the native (non-web) branch; the web branch remains for desktop-web
 * callers only.
 *
 * Curried so it can be passed directly to an `onPress` handler:
 *
 *   <Text onPress={openPolicy(doc.documentUrl)} role='link'>…</Text>
 *
 * Policy links sit inside a checkbox label, so tapping one would otherwise
 * toggle the parent checkbox. On web gluestack renders the checkbox Root as a
 * `<label>` wrapping a hidden `<input type="checkbox">`, so a click anywhere
 * inside it triggers the browser's label→control activation — that's a default
 * action, not event bubbling, so `preventDefault` (not `stopPropagation`) is
 * what suppresses it. We call both: preventDefault for the web label toggle,
 * stopPropagation for native, where the press would bubble to the Pressable
 * Root. The `event` parameter is typed `unknown` so the returned handler fits
 * both signatures it's passed to: RN-Web's SyntheticEvent and native
 * `GestureResponderEvent`. Duck-typed at runtime.
 */
export const openPolicy = (url: string) => (event?: unknown) => {
  if (event && typeof event === 'object') {
    const e = event as {
      preventDefault?: () => void;
      stopPropagation?: () => void;
      nativeEvent?: { preventDefault?: () => void; stopPropagation?: () => void };
    };
    e.preventDefault?.();
    e.nativeEvent?.preventDefault?.();
    e.stopPropagation?.();
    e.nativeEvent?.stopPropagation?.();
  }
  if (!url) return;
  if (Platform.OS === 'web') {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    WebBrowser.openBrowserAsync(url);
  }
};
